import { useId, useState, type FormEvent } from "react";
import { useCreateSSOInvite, useCreateUser } from "../../../api/queries";
import type { AuthMethod, UserRole } from "../../../api/types";
import { Button } from "../../../components/Button";
import { Modal } from "../../../components/Modal";
import { useToast } from "../../../toast/ToastContext";
import "../../../components/Form.css";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string; hint: string }[] = [
  { value: "admin", label: "Admin", hint: "Full control, including user and audit-log administration." },
  { value: "operator", label: "Operator", hint: "Day-to-day management — no user admin, no host-escape ops." },
  { value: "viewer", label: "Viewer", hint: "Read-only access." },
  {
    value: "billing_admin",
    label: "Billing admin",
    hint: "Billing only — pay, change the plan, see the machine count. No access to containers, secrets, or hosts.",
  },
];

/**
 * "New user" — Password (a real, local credential the admin sets here) vs SSO
 * (ADR-0037 §4: an INVITE, no credential; the account can't sign in until its
 * first verified sign-in through the tenant's identity provider links it).
 * The two paths call different endpoints (`createUser` vs `createSSOInvite`)
 * because they're genuinely different operations, not the same request with
 * a field toggled — SSO invites carry no password and no role choice (every
 * invite is Viewer-default, §6; an admin raises the role afterward from
 * Users, same as any other account).
 */
export function CreateUserModal({ open, onClose }: CreateUserModalProps) {
  const usernameId = useId();
  const displayNameId = useId();
  const roleId = useId();
  const passwordId = useId();
  const emailId = useId();
  const { pushToast } = useToast();
  const createUserMutation = useCreateUser();
  const createInviteMutation = useCreateSSOInvite();

  const [method, setMethod] = useState<AuthMethod>("password");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<UserRole>("operator");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");

  const mutation = method === "password" ? createUserMutation : createInviteMutation;

  function reset() {
    setMethod("password");
    setUsername("");
    setDisplayName("");
    setRole("operator");
    setPassword("");
    setEmail("");
  }

  function handleClose() {
    reset();
    createUserMutation.reset();
    createInviteMutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedUsername = username.trim();

    if (method === "password") {
      createUserMutation.mutate(
        { username: submittedUsername, displayName: displayName.trim(), role, password },
        {
          onSuccess: (created) => {
            pushToast({ title: `User "${created.username}" created.`, tone: "success" });
            handleClose();
          },
          onError: (err) => {
            pushToast({
              title: `Couldn't create "${submittedUsername}"`,
              description: err instanceof Error ? err.message : "Try again in a moment.",
              tone: "error",
            });
          },
        },
      );
      // Handed off to the mutation above; no reason to keep it in state a
      // moment longer, success or not (same reasoning as CreateSecretModal).
      setPassword("");
      return;
    }

    createInviteMutation.mutate(
      { username: submittedUsername, displayName: displayName.trim(), email: email.trim() },
      {
        onSuccess: (invited) => {
          pushToast({
            title: `"${invited.username}" invited.`,
            description: "They'll be able to sign in once they complete their first sign-in through your identity provider.",
            tone: "success",
          });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't invite "${submittedUsername}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const canSubmit =
    Boolean(username.trim() && displayName.trim()) &&
    (method === "password" ? password.length >= 8 : email.trim().length > 0);

  return (
    <Modal open={open} title="New user" onClose={handleClose} initialFocusId={usernameId}>
      <form onSubmit={handleSubmit} noValidate>
        <fieldset className="form-fieldset form-field">
          <legend className="form-field__label">Sign-in method</legend>
          <div className="form-fieldset__options">
            <label>
              <input
                type="radio"
                name="method"
                value="password"
                checked={method === "password"}
                onChange={() => setMethod("password")}
              />
              Password
            </label>
            <label>
              <input type="radio" name="method" value="sso" checked={method === "sso"} onChange={() => setMethod("sso")} />
              Single sign-on
            </label>
          </div>
          <span className="form-field__hint">
            {method === "password"
              ? "A local account with a password you set here."
              : "Invites the account — it can't sign in until they complete their first sign-in through your identity provider."}
          </span>
        </fieldset>

        <label className="form-field" htmlFor={usernameId}>
          <span>Username</span>
          <input
            id={usernameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            maxLength={64}
          />
          <span className="form-field__hint">
            {method === "password" ? "What they sign in with." : "Their internal handle in this admin UI."} Letters,
            digits, '.', '_', '-' only.
          </span>
        </label>

        <label className="form-field" htmlFor={displayNameId}>
          <span>Display name</span>
          <input
            id={displayNameId}
            type="text"
            autoComplete="off"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={128}
          />
        </label>

        {method === "password" ? (
          <label className="form-field" htmlFor={roleId}>
            <span>Role</span>
            <select id={roleId} value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="form-field__hint">{ROLE_OPTIONS.find((o) => o.value === role)?.hint}</span>
          </label>
        ) : (
          <label className="form-field" htmlFor={emailId}>
            <span>Email</span>
            <input
              id={emailId}
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <span className="form-field__hint">
              The VERIFIED email their identity provider must assert on first sign-in — this is what links their
              account, not what they type anywhere.
            </span>
          </label>
        )}

        {method === "password" ? (
          <label className="form-field" htmlFor={passwordId}>
            <span>Password</span>
            <input
              id={passwordId}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <span className="form-field__hint">
              At least 8 characters. Write-only — nobody, including admins, can read it back later.
            </span>
          </label>
        ) : (
          <p className="form-field__hint">
            New SSO users start as <strong>Viewer</strong>. Raise their role from Users after their first sign-in.
          </p>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !canSubmit}>
            {mutation.isPending ? (method === "password" ? "Creating…" : "Inviting…") : method === "password" ? "Create user" : "Invite user"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
